import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { SHA256 } from 'crypto-js';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { StateCleanupService } from './state-cleanup.service';

export interface User {
  client_id: string;
  created_at: string; // Consider using Date if you plan to handle this as a Date object
  email: string;
  id: string;
  must_change_password: boolean;
  last_login: string | null;
  name: string;
  password_hash: string;
  reset_token: string;
  reset_token_expiry: string; // Consider using Date if you plan to handle this as a Date object
  role: string;
  updated_at: string; // Consider using Date if you plan to handle this as a Date object
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  me$ = new BehaviorSubject<User | undefined>(undefined);
  users$ = new BehaviorSubject<User[]>([]);

  constructor(
    private authService: AuthService,
    protected http: HttpClient,
    protected nbToastrService: NbToastrService,
    protected stateCleanupService: StateCleanupService
  ) {
    // Keep user state in sync with the latest access token payload
    this.authService.accessTokenPayload$.subscribe((payload) => {
      if (!payload) {
        this.me$.next(undefined);
        return;
      }

      this.me$.next({
        client_id: payload.client_id,
        email: payload.email,
        id: payload.sub,
        must_change_password: false,
        name: payload.name,
        role: payload.role,
      } as User);
    });

    this.stateCleanupService.register(() => this.resetState());
  }

  deleteUser(userId: string) {
    return this.http.delete(`${environment.userBackend}/users/${userId}`).pipe(
      tap(() => {
        this.users$.next(
          this.users$.value.filter((user) => user.id !== userId)
        );
      })
    );
  }

  updateUser(user: User) {
    return this.http
      .patch<{ data: User }>(
        `${environment.userBackend}/users/${user.id}`,
        user
      )
      .pipe(
        map((data) => data.data),
        tap((updatedUser) => {
          this.users$.next(
            this.users$.value.map((user) =>
              user.id === updatedUser.id ? updatedUser : user
            )
          );
          // PATCH response only carries {id, name, email, role} — merge so
          // client_id & co. survive on me$.
          this.me$.next({ ...this.me$.value, ...updatedUser });
        })
      );
  }

  listUsers() {
    return this.http
      .get<{ data: User[] }>(`${environment.userBackend}/users`)
      .pipe(
        map((data) => data.data),
        tap((users) => {
          this.users$.next(users);
          // Enrich me$ only when the list actually contains the logged-in
          // user. A super_admin browsing a foreign client is NOT in that
          // client's user list — overwriting me$ with undefined here made
          // the dashboard header crash on every change-detection cycle.
          const me = users.find((user) => user.email === this.me$.value?.email);
          if (me) {
            this.me$.next(me);
          }
        })
      );
  }

  listAllUsers() {
    return this.http
      .get<{ data: User[] }>(`${environment.userBackend}/admin/users`)
      .pipe(
        map((data) => data.data),
        tap((users) => {
          this.users$.next(users);
          // Same guard as listUsers(): never downgrade me$ to undefined.
          const me = users.find((user) => user.email === this.me$.value?.email);
          if (me) {
            this.me$.next(me);
          }
        })
      );
  }

  getAvatar(email: string): string {
    return (
      'https://www.gravatar.com/avatar/' +
      SHA256(email).toString().toLowerCase() +
      '?d=retro&f=y'
    );
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.http
      .post(`${environment.userBackend}/password/change`, {
        current_password: oldPassword,
        new_password: newPassword,
        confirm_new_password: newPassword,
      })
      .pipe(
        tap(() => {
          this.nbToastrService.success(
            'Du wirst nun ausgeloggt. Bitte logge dich erneut ein.',
            'Passwort geändert.'
          );
          setTimeout(() => {
            this.authService.logout();
          }, 3000);
        })
      );
  }

  requestEmailChange(newEmail: string, currentPassword: string) {
    return this.http.post<{ message: string; data: { pending_email: string } }>(
      `${environment.userBackend}/email/change-request`,
      { new_email: newEmail, current_password: currentPassword }
    );
  }

  inviteUser(user: Partial<User>) {
    return this.http
      .post<{ data: User }>(`${environment.userBackend}/users/invite`, user)
      .pipe(map((data) => data.data));
  }

  /** Issues a fresh temporary password and mails the invitation again.
   *  Only valid while the invitee still has the temporary one — the backend
   *  answers 409 once they picked their own. */
  resendInvitation(userId: string) {
    return this.http.post<{ message: string }>(
      `${environment.userBackend}/users/${userId}/resend-invitation`,
      {}
    );
  }

  private resetState() {
    this.me$.next(undefined);
    this.users$.next([]);
  }
}
